import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { nativeSheetScrollBottomPad } from "@/lib/native/nativeSheetPadding";
import { Check } from "lucide-react";

export type NativeOption = {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
};

type NativeOptionSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  value: string;
  options: NativeOption[];
  onSelect: (value: string) => void;
  /** Stack above an open parent sheet (leadbox, dialogs). */
  nestedSheet?: boolean;
};

/** Bottom sheet option picker for native (sort, filters, etc.). */
export const NativeOptionSheet = ({
  open,
  onOpenChange,
  title,
  value,
  options,
  onSelect,
  nestedSheet = false,
}: NativeOptionSheetProps) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        expandable={false}
        nestedSheet={nestedSheet}
        className={cn(
          "native-option-sheet native-sheet-compact",
          nestedSheet && "native-nested-sheet",
        )}
      >
        <SheetHeader className="text-left">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <div
          className={cn(
            "native-sheet-compact-body mt-3 flex flex-col gap-1",
            nativeSheetScrollBottomPad,
          )}
        >
          {options.map((opt) => {
            const selected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                className={cn(
                  "flex w-full items-center justify-between rounded-lg px-4 py-3.5 text-left text-base transition-colors",
                  opt.disabled && "opacity-50 pointer-events-none",
                  selected
                    ? "bg-accent/15 text-accent font-medium"
                    : "hover:bg-muted active:bg-muted",
                )}
                disabled={opt.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (opt.disabled) return;
                  onSelect(opt.value);
                  onOpenChange(false);
                }}
              >
                <span className="flex items-center gap-3 min-w-0">
                  {opt.icon ? (
                    <span className="shrink-0 text-muted-foreground [&_svg]:h-5 [&_svg]:w-5">
                      {opt.icon}
                    </span>
                  ) : null}
                  <span className="truncate">{opt.label}</span>
                </span>
                {selected && <Check className="h-5 w-5 shrink-0 text-accent" />}
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
};
